import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';

import {
  ConnectionOptions,
  FunctionBlockConfigEvent,
  FunctionBlockFunctionResponseEvent,
  FunctionBlockLogResultEvent,
  FunctionBlockMessageEvent,
  FunctionBlockStateEvent,
  IWatchList,
  mqtt,
  MqttConnectionState,
  NetworkDeviceActivationMessageEvent,
  NetworkDeviceMessageEvent,
  NetworkDeviceStateEvent,
  UbieDisconnectedEvent,
} from '@ubie/mqttcommunication';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { HttpClient } from '@angular/common/http';

import { PluginListenerHandle } from '@capacitor/core';
import { Platform } from '@ionic/angular';
import { Globals } from '../../../globals';
import { AppUniqueIdService } from '../../app-unique-id/app-unique-id.service';
import { LogService } from '../../log/log.service';
import {
  TransportConfig,
  TransportConfigLoaderService,
} from '../../transport-config/transport-config-loader.service';
import { CommunicationListenerService } from '../communication-listen/communication-listen.service';

/**
 * service that interfaces over the native implementation on android or ios
 * this is all runing outside of ngZone! so make sure you call ngZone.run when receiving data from the subscribtions
 */

@Injectable({
  providedIn: 'root',
})
export class CommunicationCoreService implements OnDestroy {
  //#region private properties

  private readonly RECONNECT_TIMEOUT = 1000;
  private readonly RECONNECT_TIMEOUT_MULTIPLIER = 2;
  private readonly RECONNECT_TIME_OUT_MAX: number = 60000 * 5;

  private currentTimeout = this.RECONNECT_TIMEOUT;

  private config?: TransportConfig;

  private readonly uniqueIdService: AppUniqueIdService =
    inject(AppUniqueIdService);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly platform: Platform = inject(Platform);
  private readonly logService: LogService = inject(LogService);
  private readonly configService: TransportConfigLoaderService = inject(
    TransportConfigLoaderService,
  );
  private readonly http: HttpClient = inject(HttpClient);
  private readonly globals: Globals = inject(Globals);
  private readonly communicationListenerService: CommunicationListenerService =
    inject(CommunicationListenerService);

  //listeners
  mqttStateChangedPluginListener: PluginListenerHandle | undefined;
  ubieDisconnectedPluginListener: PluginListenerHandle | undefined;
  ubieConnectedPluginListener: PluginListenerHandle | undefined;
  networkDeviceStatePluginListener: PluginListenerHandle | undefined;
  networkDeviceMessagePluginListener: PluginListenerHandle | undefined;
  networkDeviceActivationMessagePluginListener:
    | PluginListenerHandle
    | undefined;
  fbStatePluginListener: PluginListenerHandle | undefined;
  fbGraphPluginListener: PluginListenerHandle | undefined;
  fbMessagePluginListener: PluginListenerHandle | undefined;
  fbConfigPluginListener: PluginListenerHandle | undefined;
  fbFunctionResponsePluginListener: PluginListenerHandle | undefined;
  pushNotificationPluginListener: PluginListenerHandle | undefined;

  //#endregion

  //#region public props

  mqttState = new BehaviorSubject<boolean | null>(null);
  hasMqttConnection: boolean | null = null;

  isLoggedOut = false;

  shouldRegisterListeners = true;

  //#endregion

  // injects

  $messages: Subject<any> = new Subject<any>();

  ngOnDestroy(): void {
    this.removeAllListeners()
      .then()
      .catch((x) =>
        this.logService?.error('error in removing all listeners', x),
      );
    this.shouldRegisterListeners = true;
  }

  private async removeAllListeners(): Promise<void> {
    this.logService?.warn('removing all listeners');

    if (this.mqttStateChangedPluginListener) {
      await this.mqttStateChangedPluginListener.remove();
      this.mqttStateChangedPluginListener = undefined;
    }

    if (this.ubieDisconnectedPluginListener) {
      await this.ubieDisconnectedPluginListener.remove();
      this.ubieDisconnectedPluginListener = undefined;
    }

    if (this.ubieConnectedPluginListener) {
      await this.ubieConnectedPluginListener.remove();
      this.ubieConnectedPluginListener = undefined;
    }

    if (this.networkDeviceStatePluginListener) {
      await this.networkDeviceStatePluginListener.remove();
      this.networkDeviceStatePluginListener = undefined;
    }

    if (this.fbStatePluginListener) {
      await this.fbStatePluginListener.remove();
      this.fbStatePluginListener = undefined;
    }

    if (this.fbGraphPluginListener) {
      await this.fbGraphPluginListener.remove();
      this.fbGraphPluginListener = undefined;
    }

    if (this.fbMessagePluginListener) {
      await this.fbMessagePluginListener.remove();
      this.fbMessagePluginListener = undefined;
    }

    if (this.fbConfigPluginListener) {
      await this.fbConfigPluginListener.remove();
      this.fbConfigPluginListener = undefined;
    }

    if (this.fbFunctionResponsePluginListener) {
      await this.fbFunctionResponsePluginListener.remove();
      this.fbFunctionResponsePluginListener = undefined;
    }

    if (this.pushNotificationPluginListener) {
      await this.pushNotificationPluginListener.remove();
      this.pushNotificationPluginListener = undefined;
    }

    if (this.networkDeviceMessagePluginListener) {
      await this.networkDeviceMessagePluginListener.remove();
      this.networkDeviceMessagePluginListener = undefined;
    }

    if (this.networkDeviceActivationMessagePluginListener) {
      await this.networkDeviceActivationMessagePluginListener.remove();
      this.networkDeviceActivationMessagePluginListener = undefined;
    }
  }

  connectMqtt(): Promise<void> {
    try {
      this.isLoggedOut = false;
      return new Promise((resolve, reject) => {
        this.logService.warn('starting get config');
        this.configService
          .getConfig()
          .then((x) => {
            this.logService.warn('received config', x);

            this.config = x;

            this.doConnect()
              .then(() => resolve())
              .catch((error) => reject(error));
          })
          .catch((error) => reject(error));
      });
    } catch (error) {
      console.error(`error in connectMqtt ${error}`);
      return Promise.resolve();
    }
  }

  // register the event that listens to mqtt state
  private async RegisterOnStateEvent(): Promise<void> {
    this.mqttStateChangedPluginListener = await mqtt.addListener(
      'mqttStateChanged',
      async (connectionState: MqttConnectionState) => {
        // check if were not logged out
        if (this.isLoggedOut) {
          this.logService?.warn('logged out');
          return;
        }

        this.logService?.warn(
          'mqtt state received: ',
          connectionState.state,
          this.hasMqttConnection,
        );

        const isConnected: boolean = connectionState.state === 'online';

        if (isConnected) {
          this.currentTimeout = this.RECONNECT_TIMEOUT;
        }

        if (this.hasMqttConnection !== isConnected) {
          this.hasMqttConnection = isConnected;

          // event listeners happen outside of NgZone
          this.ngZone.run(() => {
            this.mqttState.next(isConnected);
          });
        }

        if (connectionState.state === 'unauthenticated') {
          this.logService?.warn('mqtt service - getting new mqtt user');
          this.getNewUser();
        }
      },
    );

    this.logService?.warn('registered mqtt state event');
  }

  //#region public methods

  /**
   * connect the client
   */
  private async doConnect(): Promise<void> {
    this.logService?.fatal('starting connect');

    if (this.shouldRegisterListeners) {
      this.shouldRegisterListeners = false;
      await this.registerEvents();
    }

    if (!this.config) {
      return Promise.resolve();
    }

    const config: ConnectionOptions = {
      hostname: this.config.host,
      username: this.config.user || '',
      password: this.config.pass || '',
      version: this.globals.version,
      clientId: this.uniqueIdService.getUniqueId(),
    };
    return mqtt.connect(config);
  }

  normalDisconnect(): Promise<void> {
    return new Promise((resolve) => {
      mqtt.clear().then(() => {
        this.logService?.info('mqtt cleared');
      });
      resolve();
    });
  }

  disconnectWithLogOut(): Promise<void> {
    console.warn('settings loggedOut to true');
    this.isLoggedOut = true;
    this.communicationListenerService.removeAll();
    return this.normalDisconnect();
  }

  disconnectWithoutLogout(): Promise<void> {
    return this.normalDisconnect();
  }

  /**
   * send a message with QOS 0 to a specific topic
   * @param topic the topic of a mqtt message
   * @param message the message as a json string to send
   */
  publish(topic: string, message: string, ubieId: string): Promise<void> {
    return mqtt.publish({ cloudId: ubieId, topic: topic, payload: message });
  }

  /**
   *add a list of ubies network devices and functionsblock that the mqtt should send state events for
   * @param watchList the list of items to watch
   * @returns Promise<void>
   */
  addToWatchList(watchList: IWatchList): Promise<void> {
    return mqtt.addWatchers(watchList);
  }

  /**
   * remove a list of ubies network devices and functionsblock that the mqtt should send state events for
   * @param watchList the list of items to watch
   * @returns Promise<void>
   **/
  removeFromWatchList(watchList: IWatchList): Promise<void> {
    return mqtt.removeWatchers(watchList);
  }

  async addPushNotificationChannel(userId: number): Promise<void> {
    if (this.pushNotificationPluginListener === undefined) {
      this.pushNotificationPluginListener = await mqtt.addListener(
        'pushNotificationReceived',
        (event) => {
          console.warn('push notification channel received', event);
        },
      );
    }

    await mqtt.addPushNotificationChannel({
      userId: userId,
    });
  }

  /**
   * set a list of ubies network devices and functionsblock that the mqtt should send state events for
   *  and deletes the old list
   * @param watchList the list of items to watch
   * @returns Promise<void>
   */
  replaceWatchList(watchList: IWatchList): Promise<void> {
    return mqtt.replaceWatchers(watchList);
  }

  /**
   * register the listeners for the native events
   */
  async registerEvents(): Promise<void> {
    this.logService?.info('we registering the new listeners now');

    await this.RegisterOnStateEvent();

    this.ubieConnectedPluginListener = await mqtt.addListener(
      'ubieConnected',
      (event) => {
        this.logService?.info('listener - ubie connected', event.ubieId);

        this.communicationListenerService.setUbieDeviceState(event.ubieId, {
          isConnected: true,
          isOfflineMode: event.offlineConnected,
          serial: event.ubieId,
        });
      },
    );

    this.ubieDisconnectedPluginListener = await mqtt.addListener(
      'ubieDisconnected',
      (event: UbieDisconnectedEvent) => {
        this.logService?.warn('listener - ubie disconnected', event.ubieId);

        this.communicationListenerService.setUbieDeviceState(event.ubieId, {
          isConnected: false,
          isOfflineMode: false,
          serial: event.ubieId,
        });
      },
    );

    this.networkDeviceStatePluginListener = await mqtt.addListener(
      'networkDeviceStateReceived',
      (event: NetworkDeviceStateEvent) => {
        this.logService?.info('listener - network device state', event);
        this.communicationListenerService.setNetworkDeviceState(
          event.ubieId,
          event.networkDeviceId,
          event,
        );
      },
    );

    this.fbStatePluginListener = await mqtt.addListener(
      'functionBlockStateReceived',
      (state: FunctionBlockStateEvent) => {
        this.communicationListenerService.setFunctionBlockState(
          state.ubieId,
          state.networkDeviceId,
          state.functionBlockId,
          state,
        );
      },
    );

    this.fbConfigPluginListener = await mqtt.addListener(
      'functionBlockConfigReceived',
      (config: FunctionBlockConfigEvent) => {
        this.communicationListenerService.setFunctionBlockConfig(
          config.ubieId,
          config.networkDeviceId,
          config.functionBlockId,
          config,
        );
      },
    );

    this.fbGraphPluginListener = await mqtt.addListener(
      'graphRecieved',
      (baseMessage: FunctionBlockLogResultEvent) => {
        try {
          this.communicationListenerService.setLogResult(
            baseMessage.ubieId,
            baseMessage.networkDeviceId,
            baseMessage.functionBlockId,
            baseMessage,
          );
        } catch (error) {
          this.logService?.error('fbGraphPluginListener- error', error);
        }
      },
    );

    this.fbFunctionResponsePluginListener = await mqtt.addListener(
      'functionBlockFunctionResponseReceived',
      (response: FunctionBlockFunctionResponseEvent) => {
        this.communicationListenerService.setFunctionBlockFunctionResponse(
          response,
        );
      },
    );

    this.fbMessagePluginListener = await mqtt.addListener(
      'functionBlockMessageReceived',
      (message: FunctionBlockMessageEvent) => {
        this.communicationListenerService.setFunctionBlockMessage(
          message.ubieId,
          message.networkDeviceId,
          message.functionBlockId,
          message.payload,
        );
      },
    );

    this.networkDeviceMessagePluginListener = await mqtt.addListener(
      'networkDeviceMessageReceived',
      (message: NetworkDeviceMessageEvent) => {
        this.communicationListenerService.setNetworkDeviceMessage(message);
      },
    );

    this.networkDeviceActivationMessagePluginListener = await mqtt.addListener(
      'networkDeviceActivationMessageReceived',
      (message: NetworkDeviceActivationMessageEvent) => {
        this.communicationListenerService.setNetworkDeviceActivationMessage(
          message,
        );
      },
    );

    this.logService?.info('we registered succesfully the new listeners');
  }

  //#endregion

  //#region private methods

  private reconnect(): void {
    this.ngZone.runOutsideAngular(() => {
      this.currentTimeout *= this.RECONNECT_TIMEOUT_MULTIPLIER;
      if (this.currentTimeout > this.RECONNECT_TIME_OUT_MAX) {
        this.currentTimeout = this.RECONNECT_TIME_OUT_MAX;
      }

      setTimeout(() => this.doConnect(), this.currentTimeout);
    });
  }

  private getNewUser(): void {
    this.logService?.warn('MQTT-SERVICE - getting new mqtt User');
    this.generateMqttUser().subscribe({
      next: (result) => {
        this.logService?.warn('MQTT-SERVICE - received new mqtt User', result);
        const localStorageTokenName = this.globals.localStorageTokenName;
        const currentUserFromStorage = localStorage.getItem(
          localStorageTokenName || '',
        );
        const currentUser = currentUserFromStorage
          ? JSON.parse(currentUserFromStorage)
          : null;
        currentUser.mqttUsername = result.userId;
        currentUser.mqttPassword = result.pwd;
        localStorage.setItem(
          this.globals.localStorageTokenName,
          JSON.stringify(currentUser),
        );

        if (this.config) {
          this.config.user = result.userId;
          this.config.pass = result.pwd;
        }

        this.reconnect();
      },
      error: (error) => {
        this.logService?.error(
          'MQTT-SERVICE - error getting new mqtt user ',
          error,
        );
      },
    });
  }

  /**
   * Generate a new user for the mqtt server
   */
  private generateMqttUser(): Observable<any> {
    const url = `${this.globals.apiRoot}/jwtmqtt/generate`;
    return this.http.post<any>(url, null);
  }

  //#endregion
}
